import {
  BOSS_ARENA_IDS,
  BOSS_ATTACK_KINDS,
  BOSS_PLATE_ZONES,
  BOSS_STATUSES,
  bossAttackDefaults,
  bossDefaults,
  bossPhaseDefaults,
  bossPlateDefaults,
  withDefaults,
} from '../../lib/teamsDefaults';
import {
  issuesUnder,
  type BossAttackDef,
  type BossDef,
  type BossPhaseDef,
  type BossPlateDef,
} from '../../lib/validate';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  JsonListField,
  ListEditor,
  NumberField,
  SelectField,
  SelectInput,
  StackListEditor,
  TextField,
} from './fields';
import { draftStore } from '../../lib/draft';
import { bossMissionId, bossTriggerAndMission, bossTriggerId } from './defaults';
import { useContentBundle, useContentRefs } from './refs';
import type { FormProps } from './types';

type Boss = BossDef;

const arenaHints: Record<string, string> = {
  chapel: 'Mother Hush: a chapel nave with pews for low cover.',
  site: 'The Foreman: a construction warehouse, three pillars and two roller doors.',
  depot: 'The Conductor: a transit depot, a signal box in the middle and PA speakers on the walls.',
};

const statusHints: Record<string, string> = {
  live: 'On the website with a QR code; scannable.',
  coming_soon: 'A teaser: a silhouette and "Arrives …", no code.',
  retired: 'Off the website, and its codes say so.',
};

/** A boss (shared-cs `BossDef`, ADR 0028). Fields an older entry doesn't have show their C# defaults. */
export function BossForm({ value, onChange, errors }: FormProps<Boss>) {
  const { lootTables, zombies } = useContentRefs();
  const b = withDefaults(bossDefaults(), value);
  const set = (patch: Partial<Boss>) => onChange({ ...value, ...patch });
  const text = (
    k: keyof Boss,
    label: string,
    opts: { hint?: string; mono?: boolean; multiline?: boolean } = {},
  ) => (
    <TextField
      label={label}
      hint={opts.hint}
      mono={opts.mono}
      multiline={opts.multiline}
      value={b[k] as string | undefined}
      issues={errors[k]}
      onChange={(v) => set({ [k]: v })}
    />
  );
  const num = (
    k: keyof Boss,
    label: string,
    opts: { integer?: boolean; hint?: string; min?: number; max?: number; step?: number } = {},
  ) => (
    <NumberField
      label={label}
      hint={opts.hint}
      integer={opts.integer}
      min={opts.min ?? 0}
      max={opts.max}
      step={opts.step}
      value={b[k] as number | undefined}
      issues={errors[k]}
      onChange={(v) => set({ [k]: v ?? 0 })}
    />
  );
  const tableOptions = lootTables.map((t) => ({ value: t.id ?? '', label: t.id ?? '' }));
  const zombieOptions = zombies.map((z) => ({ value: z.id ?? '', label: z.id ?? '' }));
  const antechamber = b.antechamberZombieIds ?? [];

  return (
    <>
      <BossTriggerAction boss={b} />
      <Fieldset legend="Boss">
        {text('id', 'Id', { mono: true })}
        {text('name', 'Name')}
        {text('slug', 'Slug', {
          mono: true,
          hint: 'The website path /bosses/{slug}: lowercase letters, digits, single hyphens.',
        })}
        {text('codePrefix', 'Code prefix', {
          mono: true,
          hint: '2-12 capital letters, e.g. HUSH. Cosmetic only; it carries no authority.',
        })}
        <SelectField
          label="Status"
          value={b.status}
          options={BOSS_STATUSES.map((s) => ({ value: s, label: s }))}
          hint={statusHints[b.status ?? '']}
          issues={errors.status}
          onChange={(v) => set({ status: v ?? '' })}
        />
        <div>
          <CheckboxField
            label="Playable"
            hint="The fight exists in the app. Only while live, with max health and a cache table."
            checked={!!b.playable}
            onChange={(on) => set({ playable: on })}
          />
          {errors.playable?.map((i, n) => (
            <p key={n} role="alert" className="mt-0.5 text-xs text-blam">
              {i.message}
            </p>
          ))}
        </div>
        {num('difficulty', 'Difficulty', {
          integer: true,
          min: 1,
          max: 5,
          hint: '1 (gentle) to 5.',
        })}
        {num('challenge', 'Skill challenge', {
          integer: true,
          min: 1,
          max: 10,
          hint: 'ADR 0066: 1..10, how much a kill teaches (default 10).',
        })}
        {num('recommendedTeam', 'Recommended team', {
          integer: true,
          min: 1,
          hint: 'Display only.',
        })}
      </Fieldset>
      <Fieldset legend="Poster and briefing">
        {text('tagline', 'Tagline')}
        {text('arrivesLabel', 'Arrives label', {
          hint: 'Coming-soon teaser, e.g. "Arrives this winter". Empty = "Coming soon".',
        })}
        {text('lore', 'Lore', { multiline: true })}
        {text('rewardsSummary', 'Rewards summary', { multiline: true })}
        {text('artKey', 'Art key', { mono: true })}
        {text('trophyId', 'Trophy id', { mono: true, hint: 'Granted once, ever. Empty = none.' })}
        {text('trophyName', 'Trophy name')}
      </Fieldset>
      <Fieldset legend="Attempts">
        {num('energyCost', 'Energy cost', { hint: 'Spent by the fighting team at the start.' })}
        {num('cooldownHours', 'Cooldown (h)', {
          hint: 'One attempt per player per boss per this many hours.',
        })}
      </Fieldset>
      <Fieldset legend="The fight">
        {num('maxHealth', 'Max health', { integer: true, hint: 'For a solo leader.' })}
        {num('headshotMult', 'Headshot multiplier', { step: 0.05 })}
        {num('stealthOpenerFrac', 'Stealth opener fraction', {
          max: 1,
          step: 0.01,
          hint: '0..1 of max health on an unaware boss (never a kill).',
        })}
        {num('arenaRooms', 'Arena rooms', {
          integer: true,
          min: 1,
          hint: 'Antechamber(s) plus the arena.',
        })}
        {num('minClearSeconds', 'Min clear (s)', {
          hint: 'A faster defeat counts as extracted, not defeated.',
        })}
      </Fieldset>
      <Fieldset legend="Arena">
        <SelectField
          label="Arena"
          value={b.arenaId}
          none="(none)"
          options={BOSS_ARENA_IDS.map((a) => ({ value: a, label: a }))}
          hint={
            arenaHints[b.arenaId ?? ''] ?? 'The hand-authored arena. A playable boss needs one.'
          }
          issues={errors.arenaId}
          onChange={(v) => set({ arenaId: v ?? '' })}
        />
        <ListEditor
          label="Antechamber zombies"
          hint="Up to 3, waiting in the antechamber rooms."
          items={antechamber}
          what="zombie"
          issues={errors.antechamberZombieIds}
          make={() => zombies[0]?.id ?? ''}
          onChange={(next) => set({ antechamberZombieIds: next })}
          row={(z, j, setRow) => (
            <>
              <SelectInput
                label={`Antechamber zombie ${j + 1}`}
                value={z}
                options={zombieOptions}
                invalid={!!errors[`antechamberZombieIds.${j}`]?.length}
                onChange={(v) => setRow(v ?? '')}
              />
              <IssueList issues={errors[`antechamberZombieIds.${j}`]} />
            </>
          )}
        />
      </Fieldset>
      <Fieldset legend="Body and senses">
        {num('modelScale', 'Model scale', {
          step: 0.05,
          hint: 'The Brute rig scaled; 1 = a Brute.',
        })}
        {num('moveSpeedMps', 'Move speed (m/s)', { step: 0.1 })}
        {num('sightRangeM', 'Sight range (m)', { step: 0.5, hint: '0 = blind (hunts by sound).' })}
        {num('sightHalfAngleDeg', 'Sight half angle (°)', { max: 180 })}
        {num('hearingMult', 'Hearing multiplier', {
          step: 0.1,
          hint: 'Multiplies every noise radius the boss hears.',
        })}
        {num('lureMult', 'Lure multiplier', {
          step: 0.1,
          hint: "x a firecracker lure's pulses on top of hearing (Mother Hush 3); a lure never starts the fight",
        })}
        {num('knockbackResist', 'Knockback resist', {
          max: 1,
          step: 0.05,
          hint: "0..1 of a weapon's knockback shrugged off.",
        })}
        {num('weakSpotMult', 'Weak spot multiplier', {
          min: 1,
          step: 0.1,
          hint: 'Hits from behind. 1 = no weak spot.',
        })}
        {num('weakSpotArcDeg', 'Weak spot arc (°)', {
          max: 360,
          hint: '0..360 behind the boss (90 = within 45° of straight behind).',
        })}
        {num('exposedMult', 'Exposed multiplier', {
          min: 1,
          step: 0.1,
          hint: 'Damage on a stunned boss. 1 = none.',
        })}
        {num('stealthStaggerS', 'Stealth stagger (s)', {
          step: 0.1,
          hint: 'A stealth opener staggers the boss this long.',
        })}
      </Fieldset>
      <Fieldset legend="Speakers">
        {num('speakerCount', 'Speaker count', {
          integer: true,
          max: 5,
          hint: 'PA speakers around the arena (the Conductor). 0 = none, at most 5.',
        })}
        {num('speakerHealth', 'Speaker health', { integer: true })}
        {num('speakerRingS', 'Ring interval (s)', {
          hint: "Seconds between the bell's rings while any speaker stands.",
        })}
        {num('speakerHordeFrac', 'Horde per ring', {
          max: 1,
          step: 0.01,
          hint: '0..1 of the horde meter each ring adds.',
        })}
      </Fieldset>
      <Fieldset legend="Plates, attacks and phases">
        <JsonListField<BossPlateDef>
          label="Plates"
          what="plate"
          hint={`Armor that takes damage first and breaks at 0: { id, name, zone (${BOSS_PLATE_ZONES.join(' | ')}), health }.`}
          value={b.plates as BossPlateDef[] | undefined}
          issues={issuesUnder(errors, 'plates')}
          make={bossPlateDefaults}
          onChange={(v) => set({ plates: v })}
        />
        <JsonListField<BossAttackDef>
          label="Attacks"
          what="attack"
          hint={`Telegraphed attacks: { id, word, kind (${BOSS_ATTACK_KINDS.join(' | ')}), windupS, rangeM, lengthM, widthM, radiusM, arcDeg, count, damage, stunS, knockbackM, cooldownS, speedMps, wallStunS, puddleS, puddleDps, slowS, slowMult, coverNegates, usesBiteBlock, wounds }. WIND: damage (and puddleDps) is hit wind; wounds true makes a landed attack a wound instead; a grab kind goes through the push-off and bite roll.`}
          value={b.attacks as BossAttackDef[] | undefined}
          issues={issuesUnder(errors, 'attacks')}
          make={bossAttackDefaults}
          onChange={(v) => set({ attacks: v })}
        />
        <JsonListField<BossPhaseDef>
          label="Phases"
          what="phase"
          hint="Phase 1 starts the fight; a later one begins at atHealthFrac, plateBroken or afterSpeakers. Fields: id, banner, atHealthFrac, plateBroken, afterSpeakers, attacks (attack ids), speedMult, shielded, addsZombieId, addsPerWave, addsIntervalS, addsOnNoiseM, addsOnNoiseCooldownS, addsMax, visionRadiusM, channelS, channelInterruptDamage, channelHordePerS, channelCooldownS, timerS, timerHealFrac, hordeForced."
          value={b.phases as BossPhaseDef[] | undefined}
          issues={issuesUnder(errors, 'phases')}
          make={bossPhaseDefaults}
          onChange={(v) => set({ phases: v })}
        />
      </Fieldset>
      <Fieldset legend="Loot">
        <SelectField
          label="Cache loot table"
          value={b.cacheLootTableId}
          none="(none)"
          options={tableOptions}
          hint="The antechamber caches (ammo and meds)."
          issues={errors.cacheLootTableId}
          onChange={(v) => set({ cacheLootTableId: v ?? '' })}
        />
        {num('cacheLootRolls', 'Cache loot rolls', { integer: true })}
        <SelectField
          label="Repeat loot table"
          value={b.repeatLootTableId}
          none="(none)"
          options={tableOptions}
          hint="Later clears in the same run roll this instead."
          issues={errors.repeatLootTableId}
          onChange={(v) => set({ repeatLootTableId: v ?? '' })}
        />
        {num('repeatRolls', 'Repeat rolls', { integer: true })}
        <div className="sm:col-span-2">
          <StackListEditor
            label="First clear rewards"
            hint="The unique reward for the first clear in a run."
            base="firstClearRewards"
            value={b.firstClearRewards ?? undefined}
            errors={errors}
            onChange={(v) => set({ firstClearRewards: v })}
          />
        </div>
      </Fieldset>
    </>
  );
}

/**
 * "Create trigger + mission" (ADR 0029 §12): adds the boss's `boss_<bossId>` trigger and `mission_<bossId>` mission to the
 * draft when they're missing (see `bossTriggerAndMission`). Edit them afterwards on the Triggers and Missions pages.
 */
function BossTriggerAction({ boss }: { boss: Boss }) {
  const bundle = useContentBundle();
  const bossId = boss.id ?? '';
  const { trigger, mission } = bossTriggerAndMission(boss, bundle);
  const missing = [trigger && bossTriggerId(bossId), mission && bossMissionId(bossId)].filter(
    Boolean,
  );
  const create = () =>
    draftStore.update((b) => ({
      ...b,
      triggers: trigger ? [...(b.triggers ?? []), trigger] : b.triggers,
      missions: mission ? [...(b.missions ?? []), mission] : b.missions,
    }));
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded border-2 border-ink bg-white p-3 text-sm">
      <button
        type="button"
        className="rounded border-2 border-ink bg-benday px-3 py-1 font-semibold disabled:opacity-40"
        disabled={!bossId || missing.length === 0}
        onClick={create}
      >
        Create trigger + mission
      </button>
      <span className="text-xs text-bruise">
        {!bossId
          ? 'Give the boss an id first.'
          : missing.length === 0
            ? `${bossTriggerId(bossId)} and ${bossMissionId(bossId)} are in the draft.`
            : `Adds ${missing.join(' and ')} to the draft: a repeatable run-scoped start_encounter (cooldown 0; the boss keeps its own) with the boss's code prefix, and a listed WANTED mission.`}
      </span>
    </div>
  );
}
